import dayjs from "dayjs";

import { LocationReviewSummary } from "../../../../models/LocationReviewSummary";

/**
 * Parses Location Review Summary data's reviews into a format
 * that can be used to populate a chart.
 */
export const groupReviewsByLanguageThenDate = (
  locationReviewSummary: LocationReviewSummary
) => {
  // Failsafe to empty array in case languages is somehow not defined
  const languages = locationReviewSummary.languages ?? [];

  // Record language totals so we can sort out languages that had 0 reviews over the entire timespan
  const languageTotals: Record<string, number> = {
    total: 0,
    ...Object.fromEntries(languages.map((language) => [language, 0]))
  };

  // Initialize the data
  const groupedReviews: {
    review_date: string;
    languages: Record<string, number>;
  }[] = [];

  // Loop over each date in the timespan and create an empty datapoint
  const startDate = locationReviewSummary.start_date;
  const endDate = locationReviewSummary.end_date;

  let date = dayjs(startDate);

  while (!date.isAfter(endDate)) {
    groupedReviews.push({
      review_date: date.format("YYYY-MM-DD"),
      languages: {
        total: 0,
        ...Object.fromEntries(languages.map((language) => [language, 0]))
      }
    });
    date = date.add(1, "day");
  }

  // Loop over each review
  for (const processedReview of locationReviewSummary.processed_reviews) {
    const review: { review_date: string; rating: number; language: string } =
      processedReview.processed_review;
    const reviewDate = dayjs(review.review_date);

    // Find the datapoint we created earlier for this review's date
    const groupedReview = groupedReviews.find(
      (groupedReview) =>
        groupedReview.review_date === reviewDate.format("YYYY-MM-DD")
    );

    // groupedReview.languages[review.language] should always be defined because business logic
    // ensures that only languages in the list of languages are processed.
    // However it is possible on a technical level, so check here
    if (groupedReview && groupedReview.languages[review.language] != null) {
      // Add this review to the datapoint for its language, as well as adding to the total
      groupedReview.languages[review.language] += 1;
      groupedReview.languages.total += 1;

      // Add to the overall totals
      languageTotals[review.language] += 1;
      languageTotals.total += 1;
    }
  }

  // Return the grouped reviews data, as well as the overall totals
  return { groupedReviews, languageTotals };
};
